import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { convertCorrectAnswersToSAT } from '../utils/SATscoreCalculator';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import { BookOpen, Clock, Flag, Save, Play, CheckCircle } from 'lucide-react';

// Track if animations have played in this session (module-level to persist across remounts)
let hasAnimatedInSession = false;

// Card component matching GameOptionsPage design
const Card = React.memo(({ children, className = '', delay = 0, index = 0, hasAnimated }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
    const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
    
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    return (
      <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.6,
          delay: delay,
          type: "spring",
          stiffness: 100,
        }}
        whileHover={{
          scale: 1.02,
          y: -5,
          transition: { duration: 0.2 },
        }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border transition-all duration-300 ${className}`}
        style={{
          boxShadow: isHovered
            ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
            : '0 10px 40px rgba(0, 0, 0, 0.1)',
          borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
        }}
      >
        {children}
      </motion.div>
    );
});

const PracticePage = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(true);
  const [testsError, setTestsError] = useState(false);
  const sat_user = JSON.parse(localStorage.getItem('sat_user'));
  const userId = sat_user?.user_id;
  const { isDarkMode } = useContext(ThemeContext);
  const hasAnimatedRef = useRef(hasAnimatedInSession);

  // Mark animations as complete after they've had time to play
  useEffect(() => {
    const timer = setTimeout(() => {
      hasAnimatedRef.current = true;
      hasAnimatedInSession = true;
    }, 2000); // Allow enough time for initial animations to complete

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!userId) return;
    setTestsLoading(true);
    setTestsError(false);
    api.get(`/api/practice/get_practice_tests/`)
      .then(res => {
        setTests(res.data.practice_tests || []);
        setTestsLoading(false);
      })
      .catch(() => {
        setTests([]);
        setTestsError(true);
        setTestsLoading(false);
      });
  }, [userId]);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleStartClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    navigate('/sat-test', { state: { fromPractice: true } });
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  // Memoize gradient styles to prevent unnecessary re-renders
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  // Memoize gradient for section headings
  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  return (
    <div className="min-h-screen w-full flex flex-col items-center relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {/* Animated Header */}
        <motion.div
          initial={hasAnimatedRef.current ? false : { opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            SAT Practice Test
          </h1>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-xl font-zonaProRegular"
            initial={hasAnimatedRef.current ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Experience the SAT just like the real thing
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column: Rules Card */}
          <Card delay={0.2} index={1} className="space-y-6 lg:col-span-3" hasAnimated={hasAnimatedRef.current}>
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="p-2 rounded-xl bg-[#a6a6a6] text-white"
              >
                <CheckCircle size={20} />
              </motion.div>
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite'
                }}
              >
                Test Rules
              </h2>
            </div>
            <ul className="space-y-6 text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 font-zonaProRegular">
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">🎯</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Modules:</strong> The SAT Practice Test consists of 4 modules in the following order:
                  <ul className="list-decimal pl-8 mt-2 text-base sm:text-lg md:text-xl">
                    <li>Reading & Writing Module 1: 27 questions, 32 minutes</li>
                    <li>Reading & Writing Module 2: 27 questions, 32 minutes</li>
                    <li>10-minute break (optional)</li>
                    <li>Math Module 1: 22 questions, 35 minutes</li>
                    <li>Math Module 2: 22 questions, 35 minutes</li>
                  </ul>
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">⏰</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Timed Sections:</strong> Each module is timed. You must complete each module before moving to the next.
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">🔢</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Navigation:</strong> You can navigate between questions within a module using the numbered buttons or Previous/Next.
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">🚩</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Flag & Review:</strong> You can flag questions for review and change your answers before submitting the module.
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">⏳</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Auto-Submit:</strong> If time runs out, your answers will be auto-submitted for that module.
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">💾</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Progress:</strong> Your progress is saved automatically. Do not close or reload the page during the test.
                </div>
              </motion.li>
              <motion.li
                className="flex items-start"
                initial={hasAnimatedRef.current ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
              >
                <span className="text-black dark:text-white mr-3 text-xl sm:text-2xl md:text-2xl">🏁</span>
                <div className="text-left">
                  <strong className="text-black dark:text-white">Session End:</strong> After completing all modules, your session will end and you will return to the Score report page.
                </div>
              </motion.li>
            </ul>
            <motion.button
              onClick={handleStartClick}
              className="w-full py-3 px-8 bg-[#34c759] text-white text-xl sm:text-2xl md:text-2xl font-zonaProUltra rounded-full shadow-lg hover:shadow-[#34c759]/50 transition-all mt-6"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
            >
              Start Practice Test
            </motion.button>
          </Card>
          {/* Right Column: Test History */}
          <Card delay={0.1} index={0} className="space-y-6 lg:col-span-2" hasAnimated={hasAnimatedRef.current}>
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="p-2 rounded-xl bg-[#a6a6a6] text-white"
              >
                <BookOpen size={20} />
              </motion.div>
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra relative inline-block"
                style={{
                  color: isDarkMode ? '#ff7a00' : '#212D82',
                  background: sectionHeadingGradient,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shine 10s linear infinite'
                }}
              >
                Test History
              </h2>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
              {testsLoading ? (
                <div className="flex items-center justify-center min-h-[120px] text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-orange-500 mr-2"></div>
                  <span>Loading your test history...</span>
                </div>
              ) : testsError ? (
                <div className="flex items-center justify-center min-h-[120px] text-red-500 dark:text-red-400 font-semibold">Error fetching test history.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ height: 'auto' }}>
                    <thead className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm sticky top-0">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">Test #</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">Date</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">Math</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">English</th>
                        <th className="px-2 sm:px-4 py-2 text-left text-xs sm:text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300">Total Score</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-700">
                      {tests.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-xs sm:text-sm md:text-base text-gray-400 dark:text-gray-500">No practice tests found. Start a test to see your history here.</td>
                        </tr>
                      ) : (
                        tests.map((test, idx) => {
                          const mathCorrect = test.math['module-1'].correct + test.math['module-2'].correct;
                          const englishCorrect = test.english['module-1'].correct + test.english['module-2'].correct;
                          const mathScore = convertCorrectAnswersToSAT(mathCorrect, 'math');
                          const englishScore = convertCorrectAnswersToSAT(englishCorrect, 'english');
                          const totalScore = mathScore + englishScore;
                          return (
                            <motion.tr
                              key={test.test_id}
                              onClick={() => navigate('/sat-test-report', { state: { reportId: test.test_id } })}
                              className="cursor-pointer hover:bg-cyan-50 dark:hover:bg-gray-700/50 transition-colors"
                              whileHover={{ scale: 1.01 }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.05 }}
                            >
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{idx + 1}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{test.created_date ? new Date(test.created_date).toLocaleDateString() : ''}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{mathScore}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{englishScore}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm md:text-base font-semibold" style={{ color: '#ff7a00' }}>{totalScore}</td>
                            </motion.tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>

        </div>

        <motion.div
          initial={hasAnimatedRef.current ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 text-center"
        >
          <Link
            to="/options"
            className="inline-block font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-zonaProRegular text-base sm:text-lg md:text-xl transition-colors"
          >
            ← Back to Game Modes
          </Link>
        </motion.div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          onClick={handleCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 max-w-sm w-full mx-4 text-center border border-white/20 dark:border-gray-700/50"
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra dark:text-gray-200 mb-3 sm:mb-4" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}>Start Practice Test?</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 dark:text-gray-300 mb-4 sm:mb-6">Are you sure you want to start the SAT practice test? You will not be able to pause or restart once you begin.</p>
            <div className="flex justify-center gap-3 sm:gap-4">
              <motion.button
                onClick={handleCancel}
                className="px-4 sm:px-6 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm sm:text-base font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleConfirm}
                className="px-4 sm:px-6 py-2 rounded-lg bg-[#34c759] text-white text-sm sm:text-base font-semibold hover:shadow-lg hover:shadow-[#34c759]/50 transition"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
              >
                Start Test
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default PracticePage;
